import {
  AdditionalFrequency,
  AggregationModelEnum,
  IPriceReqSchema,
  IPricesAndOptionsReqSchema,
  IProduct,
  IProductOptionSchema,
  IRateReqSchema,
  OfferingTypesEnum,
  PriceModelEnum,
  ProductTypeEnum,
  RateBillingFrequencyEnum,
  RateUsageBillingFrequencyEnum,
} from '@monetize/types/app';
import { DEFAULT_PAGER, TDataTablePager } from '@monetize/types/common';
import { MdInfo } from 'react-icons/md';
import {
  MFlex,
  MIcon,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { PRICE_MODEL_OPTIONS } from '../../../../../constants/offerings';
import { PRODUCT_TYPES_WITH_INITIAL_QUANTITY_ZERO } from '../../../../../constants/products';
import { arrayToObject } from '../../../../../utils/misc';

export const renderPriceModelItemContent = ({
  title,
  item,
  isSubtitle,
  isSelected,
  isHighlight,
}: {
  title: string;
  item: any;
  isSubtitle: boolean;
  isSelected: boolean;
  isHighlight: boolean;
}) => (
  <PriceModelItemContent
    title={title}
    item={item}
    isSubtitle={isSubtitle}
    isSelected={isSelected}
    isHighlight={isHighlight}
  />
);

export const getPriceModelHelperText = (title: PriceModelEnum) => {
  switch (title) {
    case PriceModelEnum.FLAT:
      return 'Flat price regardless of the number of units';

    case PriceModelEnum.TIERED:
      return 'The total price is calculated cumulatively. For each tier, multiply the number of units in that tier by its price, then add these amounts together';

    case PriceModelEnum.VOLUME:
      return 'All units are charged at the price tier corresponding to the total volume';

    case PriceModelEnum.CUSTOM:
      return 'The price is manually specified during the sales process';

    case PriceModelEnum.BLOCK:
      return 'Pricing is based on predefined quantity blocks. You can choose  to keep all blocks the same size and price, or define custom sizes and rates for each block.';

    default:
      return '';
  }
};

const PriceModelItemContent = ({
  title,
  item,
  isSubtitle,
  isSelected,
  isHighlight,
}: {
  title: string;
  item: any;
  isSubtitle: boolean;
  isSelected: boolean;
  isHighlight: boolean;
}) => {
  return (
    <MFlex
      alignItems="center"
      justifyContent="space-between"
      w="100%"
      position="relative"
      role="group"
      _hover={{
        '& div': {
          display: 'block',
        },
      }}
    >
      <MText
        color="inherit"
        truncate
        lineClamp={1}
        fontSize={isSubtitle ? '12px' : 'sm'}
        mb="-2px"
        mt="0px"
        fontWeight={isSelected || isHighlight ? 'bold' : 'light'}
      >
        {PRICE_MODEL_OPTIONS[title as PriceModelEnum] || title}
      </MText>

      <MTooltip
        label={getPriceModelHelperText(item.value)}
        placement="bottom-start"
      >
        <MFlex position="relative" alignItems="center" minH="5">
          <MIcon as={MdInfo} color="tPurple.base" />
        </MFlex>
      </MTooltip>
    </MFlex>
  );
};

/**
 * Convert an object of prices by productId into a combined array of prices
 */
export function getPricesFromProductObject(
  products: Record<string, IPricesAndOptionsReqSchema>,
): IPriceReqSchema[] {
  if (!products) {
    return [];
  }
  return Object.keys(products)
    .reduce(
      (acc: IPriceReqSchema[], curr: string) =>
        products ? [...acc, ...(products[curr].prices as any)] : acc,
      [],
    )
    .map(
      ({
        productId,
        from,
        to,
        amount,
        description,
        priceModel,
        blockSize,
      }: IPriceReqSchema) => ({
        productId,
        from,
        to,
        amount,
        description,
        priceModel,
        blockSize,
      }),
    );
}
function determineProductInclusion({
  isUsageProduct,
  offeringType,
  isAnyUsageProductExist,
}: {
  isUsageProduct: boolean;
  offeringType?: OfferingTypesEnum;
  isAnyUsageProductExist?: boolean;
}): boolean {
  // If there are any usage products, include all products
  if (isAnyUsageProductExist) {
    return true;
  }

  // For subscription offerings without usage products, include non-usage products
  if (offeringType === OfferingTypesEnum.SUBSCRIPTION && !isUsageProduct) {
    return true;
  }

  // For subscription offerings, include usage products only if they exist
  if (offeringType === OfferingTypesEnum.SUBSCRIPTION && isUsageProduct) {
    return true; // or could be conditional based on your business logic
  }

  return false;
}
/**
 * Convert aggregationModel by productId into a combined array of options
 */
export function getOptionsFromProductObject({
  products,
  offeringType,
  isAnyUsageProductExist,
}: {
  products: Record<string, IPricesAndOptionsReqSchema>;
  offeringType?: OfferingTypesEnum;
  isAnyUsageProductExist?: boolean;
}): IProductOptionSchema[] {
  if (!products) {
    return [];
  }

  return Object.keys(products)
    .reduce((acc: IProductOptionSchema[], productId: string) => {
      const product = products[productId];
      const isUsageProduct = product.productType === ProductTypeEnum.USAGE;

      // Determine if we should include this product based on offering type and usage
      const shouldInclude = determineProductInclusion({
        isUsageProduct,
        offeringType,
        isAnyUsageProductExist,
      });

      if (!shouldInclude) {
        return acc;
      }

      return [
        ...acc,
        {
          productId,
          aggregationModel: product.aggregationModel as AggregationModelEnum,
        },
      ];
    }, [])
    .filter(({ aggregationModel }) => !!aggregationModel);
}

export const PRODUCTS_ITEM_DEFAULT_PAGER: TDataTablePager = {
  ...DEFAULT_PAGER,
  rows: 50,
  sortField: 'name',
  sortOrder: 1,
};

/**
 *
 * @param selectedPriceModel - Currently selected price model in 0th index
 * @param disabledPriceModel - Disabled price model that user is trying to select
 * @returns - Constructs the message based on the params and returns it.
 */
export const getTooltipDisabledMessage = ({
  selectedPriceModel,
  disabledPriceModel,
}: {
  selectedPriceModel: string;
  disabledPriceModel: string;
}) => {
  if (
    selectedPriceModel === PRICE_MODEL_OPTIONS.FLAT &&
    disabledPriceModel === PRICE_MODEL_OPTIONS.TIERED
  ) {
    return `${disabledPriceModel} pricing cannot be used with ${selectedPriceModel} + ${PRICE_MODEL_OPTIONS.VOLUME} pricing. To change to ${disabledPriceModel} pricing, change the first pricing model to ${disabledPriceModel}.`;
  }

  if (
    selectedPriceModel === PRICE_MODEL_OPTIONS.FLAT &&
    disabledPriceModel === PRICE_MODEL_OPTIONS.VOLUME
  ) {
    return `${disabledPriceModel} pricing cannot be used with ${selectedPriceModel} + ${PRICE_MODEL_OPTIONS.TIERED} pricing. To change to ${disabledPriceModel} pricing, change the first pricing model to ${disabledPriceModel}.`;
  }

  return `${disabledPriceModel} pricing cannot be used with ${selectedPriceModel} pricing. To change to ${disabledPriceModel} pricing, change the first pricing model to ${disabledPriceModel}.`;
};

/**
 *
 * @param products - list of selected products
 * @returns - returns an object of each productTypes with true/false value based on there existence
 */
export const getProductTypeExistence = (products: IProduct[]) => {
  let isSubscriptionProductTypeExist = false;
  let isMinCommitProductTypeExist = false;
  let isOneTimeProductTypeExist = false;
  let isOneTimePrepaidCreditProductTypeExist = false;
  let isUsageProductTypeExist = false;

  for (const product of products) {
    switch (product.productType) {
      case ProductTypeEnum.ADVANCE:
        isSubscriptionProductTypeExist = true;
        break;
      case ProductTypeEnum.MIN_COMMIT:
        isMinCommitProductTypeExist = true;
        break;
      case ProductTypeEnum.ONETIME:
        isOneTimeProductTypeExist = true;
        break;
      case ProductTypeEnum.ONETIME_PREPAID_CREDIT:
        isOneTimePrepaidCreditProductTypeExist = true;
        break;
      case ProductTypeEnum.USAGE:
        isUsageProductTypeExist = true;
        break;
      default:
        break;
    }
  }

  return {
    isSubscriptionProductTypeExist,
    isMinCommitProductTypeExist,
    isOneTimeProductTypeExist,
    isOneTimePrepaidCreditProductTypeExist,
    isUsageProductTypeExist,
  };
};

/**
 *
 * @param isOneTime - is Offering type is one time
 * @param isMinCommit - is Offering type is min commit
 * @param isSubscription - is Offering type is subscription
 * @param isSubscriptionProductTypeExist - is subscription type product exists in selected list products
 * @returns - Billing Frequency or undefined based on the params
 */
export const getDefaultBillingFrequency = ({
  isOneTime,
  isMinCommit,
  isPercentOfTotal,
  isSubscription,
  isSubscriptionProductTypeExist,
  isOnetimeToken,
}: {
  isOneTime: boolean;
  isMinCommit: boolean;
  isPercentOfTotal: boolean;
  isSubscription: boolean;
  isSubscriptionProductTypeExist: boolean;
  isOnetimeToken: boolean;
}) => {
  if (isOneTime || isOnetimeToken) {
    return RateBillingFrequencyEnum.ONETIME;
  }

  if (isMinCommit || isPercentOfTotal) {
    return RateBillingFrequencyEnum.MONTHLY;
  }

  /** Will make use of this logic in future */
  // if (isSubscription && isSubscriptionProductTypeExist) {
  //   return RateBillingFrequencyEnum.MONTHLY;
  // }

  // return undefined;

  return RateBillingFrequencyEnum.ANNUALLY;
};

/**
 *
 * @param isMinCommit - is Offering type is min commit
 * @param isUsageProductTypeExist - is usage type product exists in the list of selected products
 * @returns - Usage Billing Frequency or undefined based on the params
 */
export const getDefaultUsageBillingFrequency = ({
  isMinCommit,
  isUsageProductTypeExist,
}: {
  isMinCommit: boolean;
  isUsageProductTypeExist: boolean;
}) => {
  if (isMinCommit || isUsageProductTypeExist) {
    return RateUsageBillingFrequencyEnum.MONTHLY;
  }

  return undefined;
};

/**
 *
 * @param products - array of products for the offering
 * @param frequency - additional frequency
 * @param finalData - main frequency final data
 * @returns - additional frequency final data
 */
export const getAdditionalFrequencyData = (
  products: IProduct[],
  frequency: AdditionalFrequency,
  finalData: IRateReqSchema,
): IRateReqSchema => {
  const productsMap = arrayToObject(products, 'id');

  return {
    ...finalData,
    name: frequency.name,
    description: frequency.description,
    billingFrequencyInMonths: frequency.billingFrequencyInMonths,
    billingFrequency: frequency.billingFrequency,
    prices: finalData.prices.map((priceItem) => ({
      ...priceItem,
      amount: calculateAdjustedAmount(
        priceItem,
        priceItem.productId ? productsMap[priceItem.productId] : undefined,
        frequency,
        finalData,
      ),
    })),
  };
};

/**
 * Calculates the adjusted amount for a price item based on product type and frequency conversion.
 */
const calculateAdjustedAmount = (
  priceItem: IPriceReqSchema,
  product: IProduct | undefined,
  targetFrequency: AdditionalFrequency,
  sourceData: IRateReqSchema,
): number => {
  const { amount } = priceItem;

  if (!product) {
    return amount;
  }
  const { productType } = product;
  const sourceFrequencyInMonths = sourceData.billingFrequencyInMonths!;

  let adjustedAmount: number;

  switch (productType) {
    case ProductTypeEnum.ADVANCE:
      adjustedAmount =
        (amount * targetFrequency.billingFrequencyInMonths) /
        sourceFrequencyInMonths;
      break;

    case ProductTypeEnum.MIN_COMMIT:
    case ProductTypeEnum.ONETIME_PREPAID_CREDIT:
      adjustedAmount = amount;
      break;

    default:
      adjustedAmount = amount / sourceFrequencyInMonths;
      break;
  }

  return Math.round(adjustedAmount * 100) / 100;
};

export const getInitialQuantityByProductType = (
  productType: ProductTypeEnum,
): number | null => {
  return PRODUCT_TYPES_WITH_INITIAL_QUANTITY_ZERO.includes(productType)
    ? null
    : 0;
};

/**
 * Grouped, at least two decimals, never truncated (16 is the JS precision
 * ceiling `formatNumber` also caps 'max' at). Given to the number input as
 * `formatOptions` rather than pre-formatting `value`: the input parses a
 * pre-formatted "1,000.00" as 1.
 *
 * `useGrouping: true` is the default, spelled out on purpose: zag keeps one
 * formatter for every number input and only rebuilds it when the options
 * differ, comparing just the NEW options' keys. Without the key, switching
 * back from RATE_AMOUNT_EDIT_FORMAT counts as no change and the amount stays
 * ungrouped after blur.
 */
export const RATE_AMOUNT_DISPLAY_FORMAT: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 16,
  useGrouping: true,
};

/**
 * RATE_AMOUNT_DISPLAY_FORMAT without thousands separators, for the Amount
 * input while it is being edited. Keeping the decimals means focusing leaves
 * "30.00" as "30.00": zag's default format would turn it into "30", and its
 * cursor mapping would then shrink a select-all to the trailing "0", so
 * typing 20 over the field saved 320.
 */
export const RATE_AMOUNT_EDIT_FORMAT: Intl.NumberFormatOptions = {
  ...RATE_AMOUNT_DISPLAY_FORMAT,
  useGrouping: false,
};

/**
 * Left padding for an Amount input that shows its currency symbol inside, on
 * the left. One-character symbols like "$" keep the default padding; longer
 * ones like "AED" get room for every character, so the amount doesn't run
 * into them.
 */
export const getAmountPaddingStart = (
  currencySymbol: string,
  defaultPadding: number | string,
): number | string =>
  currencySymbol.length > 1
    ? `calc(${currencySymbol.length}ch + 1rem)`
    : defaultPadding;
